import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useParams, Navigate, useNavigate} from "react-router";
import styled, {css} from "styled-components";
import {useForm, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {DragDropContext, Droppable} from "react-beautiful-dnd";
import {
  faArrowLeft,
  faArrowRight,
  faEdit,
  faEllipsisVertical,
  faFilter,
  faGears,
  faMagnifyingGlassArrowRight,
  faMapMarked,
  faMinus,
  faPlus,
  faRotateLeft,
  faToggleOff,
  faToggleOn,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";

// Hooks
import useApi from "../hooks/useApi.js";
import useMountedState from "../hooks/useMountedState.js";
import usePrevious from "../hooks/usePrevious.js";
import useVisibilityConflicts from "../hooks/useVisibilityConflicts.js";

// Contexts
import {AuthContext} from "../contexts/auth.js";
import {NotificationContext} from "../contexts/notify.js";
import {FacilityNavContext} from "../contexts/facilitynav.js";
import {useToast} from "../contexts/toast.js";
import {SettingsContext} from "../contexts/settings.js";
import {NavContext} from "../contexts/nav.js";

// Utils
import {
  getSnakeCase,
  setWithExpiry,
  getWithExpiry,
  generateUniqueKey,
  prettyDateInUserTimezone,
  exists,
  toTitleCase,
  convertToUserTimezone,
  hexToRgba
} from "../utils/helpers.js";
import {
  BASE_OPTIONS,
  BASE_OPTIONS_PLURAL,
  DAY_IDX,
  DAY_NAMES_DAYJS,
  DAY_TO_IDX_DAYJS,
  getNumberSuffix,
  IDX_TO_DAY,
  IDX_TO_DAY_DAYJS
} from "./schedule/helpers.js";
import {
  addComponent,
  addField,
  removeElement,
  updateElement,
  newFromBuilder,
  validBuilder,
  addGroup,
  FIELD,
  sortHelp,
  labelAlreadyExists,
  getBuilderOverrides,
  deepCopyBuilder
} from "../utils/builder.js";
import {
  recurseToggle,
  getToggleState,
  resolveDependencyMismatches,
  canSetExternalChecksheet
} from "./checksheet-builder/helpers.js";

// Components
import {
  InputText,
  InputSelect,
  InputDay,
  InputCalendar,
  InputNumber,
  InputError
} from "../components/form/FormInputs.js";
import Select from "../components/Select.js";
import Help from "../components/Help.js";
import Room from "./general/Room.js";
import FacilityBuilder from "./FacilityBuilder.js";
import FacilityPageHeader from "./general/FacilityPageHeader.js";
import RenderChecksheetBuilder from "./checksheet-builder/RenderChecksheetBuilder.js";
import MemberRestrict from "./checksheet-builder/MemberRestrict.js";
import Notifications from "./checksheet-builder/Notifications.js";
import ChecksheetRestrictions from "./checksheet-builder/ChecksheetRestrictions.js";
import ComponentTemplates from "./checksheet-builder/ComponentTemplates.js";
import MissingElements from "./checksheet-builder/MissingElements.js";
import Modal from "../components/Modal.js";
import ModalAddField from "./general/ModalAddField.js";
import ModalBulk from "./checksheet-builder/ModalBulk.js";
import ModalEditElement from "./checksheet-builder/ModalEditElement.js";
import ModalDeleteElement from "./checksheet-builder/ModalDeleteElement.js";
import ModalRedirect from "./general/ModalRedirect.js";
import ModalFormula from "./general/ModalFormula.js";
import ModalElapsed from "./checksheet-builder/ModalElapsed.js";
import ModalConditionals from "./checksheet-builder/ModalConditionals.js";
import ModalHelp from "./checksheet-builder/ModalHelp.js";
import ModalApplyTemplate from "./checksheet-builder/ModalApplyTemplate.js";
import ModalAddToBuilder from "./checksheet-builder/ModalAddToBuilder.js";
import ModalSaveTemplate from "./checksheet-builder/ModalSaveTemplate.js";
import ModalChecksheetPreview from "./checksheet-builder/ModalChecksheetPreview.js";
import ModalNotifications from "./checksheet-builder/ModalNotifications.js";
import ModalChecksheetRestrictions from "./checksheet-builder/ModalChecksheetRestrictions.js";
import ModalFilter from "./checksheet-builder/ModalFilter.js";
import ModalMemberRestrict from "./checksheet-builder/ModalMemberRestrict.js";
import ModalDelete from "../components/ModalDelete.js";
import ModalTransfer from "./checksheet-builder/ModalTransfer.js";
import ModalAddState from "./checksheet-builder/ModalAddState.js";

// Style
import {pad, border, radius} from "../style/components/variables.js";
import {flex, z} from "../style/components/mixins.js";
import {bp} from "../style/components/breakpoints.js";
import {
  Page,
  Loader,
  Heading,
  Form,
  FormField,
  ButtonFull,
  Inline,
  ButtonLoader,
  Button,
  Abbr,
  Pill,
  Warning,
  Error,
  HeadingMedium,
  Label,
  FormFieldWrapper,
  Input,
  Select as NativeSelect,
  StickyWrapper,
  Text,
  scrollbar
} from "../style/components/general.js";

const NEVER = "Never";

const CUSTOM_OPTION = {
  name: "custom",
  label: "Custom..."
};

const DEFAULT_STATE = {
  name: "DEFAULT",
  id: "0"
};

const DEFAULT_FREQUENCY = {
  frequency: "",
  start: {
    date: null,
    visibleDate: null,
    lastDay: null
  },
  recurrence: {
    interval: 1,
    base: "days",
    days: null,
    availability: null
  }
};

const NO_LAST_DAY_OPTION = ["daily", "weekly", "bi-weekly", "work-week", "semi-monthly"];

const TEMP_CHECKSHEET_TTL_SECONDS = (process.env.REACT_APP_EXPIRATION_IN_MINUTES ?? 10) * 60;

const tempChecksheetKey = (facilitySlug, suffix) =>
  `${getSnakeCase(facilitySlug)}.temp_checksheet.${suffix}`;

const persistTempChecksheet = (facilitySlug, suffix, data) => {
  const key = tempChecksheetKey(facilitySlug, suffix);
  if (data == null) {
    setWithExpiry(key, null);
    return;
  }
  setWithExpiry(key, JSON.stringify(data), TEMP_CHECKSHEET_TTL_SECONDS);
};

const clearTempChecksheet = facilitySlug => {
  persistTempChecksheet(facilitySlug, "states", null);
  persistTempChecksheet(facilitySlug, "draft", null);
  persistTempChecksheet(facilitySlug, "notifications", null);
};

const readTempChecksheet = (facilitySlug, suffix) => {
  const raw = getWithExpiry(tempChecksheetKey(facilitySlug, suffix));
  if (raw == null || raw === "") return null;
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

const ChecksheetBuilder = () => {
  const {slug} = useParams();
  const navigate = useNavigate();

  const isMounted = useMountedState();

  const {addToast} = useToast();

  const {roleCanAccessResource} = useContext(AuthContext);
  const {getNotifications} = useContext(NotificationContext);
  const {facility, setFacility} = useContext(FacilityNavContext);
  const {allStates, setIntermediateAction} = useContext(NavContext);
  const {settings} = useContext(SettingsContext);

  const [active, setActive] = useState(null);
  const [activeDroppableId, setActiveDroppableId] = useState("");
  const [currentStateId, setCurrentStateId] = useState(DEFAULT_STATE.id);
  const [states, setStates] = useState({
    [DEFAULT_STATE.id]: {
      builder: {allIds: [], byId: {}},
      frequency: DEFAULT_FREQUENCY
    }
  });
  const [error, setError] = useState(null);
  const [expandNames, setExpandNames] = useState(false);
  const [frequencies, setFrequencies] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitsToAdd, setUnitsToAdd] = useState([]);
  const [loadingPublish, setLoadingPublish] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [showFacility, setShowFacility] = useState(false);
  const [startPreview, setStartPreview] = useState("");

  // Menu Left
  const [restrictTo, setRestrictTo] = useState([]);
  const [external, setExternal] = useState(false);
  const [fieldNotifications, setFieldNotifications] = useState({allIds: [], byId: {}});
  const [checksheetRestrictions, setChecksheetRestrictions] = useState([]);
  const visibilityConflicts = useVisibilityConflicts(
    restrictTo,
    checksheetRestrictions,
    facility?.id
  );
  const [refreshTemplates, setRefreshTemplates] = useState(false);
  const [template, setTemplate] = useState(null);
  const [appliedTemplates, setAppliedTemplates] = useState({});
  const [templateList, setTemplateList] = useState([]);
  // Menu Right
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingToggleAll, setLoadingToggleAll] = useState(false);
  const [hasToggledOff, setHasToggledOff] = useState(null);
  const [hasToggledOn, setHasToggledOn] = useState(null);
  // Filters
  const [filters, setFilters] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterResult, setFilterResult] = useState(null);
  // Modals
  const [target, setTarget] = useState(null);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalTransfer, setShowModalTransfer] = useState(false);
  const [showModalHelp, setShowModalHelp] = useState(false);
  const [showModalBulk, setShowModalBulk] = useState(false);
  const [showModalRedirectFormula, setShowModalRedirectFormula] = useState(false);
  const [showModalRedirectElapsed, setShowModalRedirectElapsed] = useState(false);
  const [formulaMode, setFormulaMode] = useState("create");
  const [showModalFormula, setShowModalFormula] = useState(false);
  const [elapsedMode, setElapsedMode] = useState("create");
  const [showModalElapsed, setShowModalElapsed] = useState(false);
  const [showModalConditional, setShowModalConditional] = useState(false);
  const [showModalTemplate, setShowModalTemplate] = useState(false);
  const [showModalSaveTemplate, setShowModalSaveTemplate] = useState(false);
  const [showModalNotification, setShowModalNotification] = useState(false);
  const [showModalRestriction, setShowModalRestriction] = useState(false);
  const [showModalMemberRestrict, setShowModalMemberRestrict] = useState(false);
  const [showModalAddFBElement, setShowModalAddFBElement] = useState(false);
  const [elementToInsert, setElementToInsert] = useState(null);
  const [refreshMissing, setRefreshMissing] = useState(true);
  const [showModalConfirmDeleteState, setShowModalConfirmDeleteState] = useState(false);
  const [showModalAddState, setShowModalAddState] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [leaveWarning, setLeaveWarning] = useState(false);
  const [queuedAction, setQueuedAction] = useState();

  const actionsMenuRef = useRef(null);

  const {api: apiFacility, loading: loadingFacility} = useApi(`facilities/${slug}`);
  const {api: apiFrequency} = useApi("frequencies", {suppress: {error: true, success: true}});
  const {api: apiChecksheet} = useApi("checksheets");
  const {api: apiBase} = useApi("base-components");
  const {api: apiUnits} = useApi("units");

  // Once-per-slug guard so leave/return restores temp builder from localStorage.
  const retrievedLocalStorage = useRef(null);
  const canPersistRef = useRef(false);
  // Non-null while reset() is hydrating a cached/state frequency so the
  // frequency-change effects do not overwrite restored recurrence/start.
  // Cleared only once frequency, base, and start date all match the hydrated
  // values — two states can share a frequency name with a different
  // base/start, so name alone isn't enough to confirm hydration landed.
  const hydratingFrequencyRef = useRef(null);

  const persistStates = useCallback(
    nextStates => {
      if (!slug || !canPersistRef.current) return;
      persistTempChecksheet(slug, "states", nextStates);
    },
    [slug]
  );

  const currentStateInfo = useMemo(() => states[currentStateId], [states, currentStateId]);
  const otherState = useMemo(
    () =>
      Object.entries(states)
        .filter(([id]) => id !== currentStateId)
        .map(([, state]) => state)[0],
    [states, currentStateId]
  );

  const builder = useMemo(() => currentStateInfo?.builder, [currentStateInfo]);
  const otherBuilder = useMemo(() => otherState?.builder, [otherState]);

  const frequency = useMemo(() => currentStateInfo?.frequency, [currentStateInfo]);

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return Object.entries(allStates)
      .filter(([, {id}]) => id != null && Number.isFinite(Number(id)))
      .map(([name, {id, color}]) => ({
        name: name.toUpperCase(),
        id: `${id}`,
        color
      }));
  }, [allStates]);

  const currentState = useMemo(
    () => facilityStates?.find(({id}) => id === currentStateId) || null,
    [currentStateId, facilityStates]
  );

  const stateId = useMemo(
    () => Object.keys(states).filter(id => id !== DEFAULT_STATE.id)[0],
    [states]
  );

  const state = useMemo(
    () => facilityStates?.find(({id}) => id === stateId) || null,
    [facilityStates, stateId]
  );

  const handleStateNameChange = useCallback(
    value => {
      if (!stateId) return;
      setStates(prev => {
        const temp = {...prev, [stateId]: {...prev[stateId], name: value}};
        persistStates(temp);
        return temp;
      });
    },
    [stateId, persistStates]
  );

  // Auto-expand the per-state name editor once if a state-specific name was
  // already set (e.g. restored from a draft / temp storage).
  const nameExpandInit = useRef(false);
  useEffect(() => {
    if (!nameExpandInit.current && stateId && states[stateId]?.name) {
      setExpandNames(true);
      nameExpandInit.current = true;
    }
  }, [stateId, states]);

  const checksheetStateSelectOptions = useMemo(() => {
    const rows = [{label: DEFAULT_STATE.name, name: DEFAULT_STATE.id}];
    const extra = Object.keys(states).filter(id => id !== DEFAULT_STATE.id);
    if (!facilityStates?.length) return rows;
    rows.push(
      ...extra
        .map(id => {
          const fs = facilityStates.find(s => s.id === id);
          return fs ? {label: fs.name, name: id, color: fs.color} : null;
        })
        .filter(fs => fs !== null)
    );
    return rows;
  }, [states, facilityStates]);

  const hasElements = useMemo(() => !!builder?.allIds?.length, [builder]);

  const schema = yup.object().shape({
    name: yup.string().required("Please provide a name."),
    frequency: yup.string().required("Please provide a frequency."),
    recurrence: yup
      .object()
      .nullable()
      .when("frequency", ([freq], s) => {
        if (freq === CUSTOM_OPTION.name)
          return yup.object().shape({
            interval: yup
              .number()
              .transform((v, o) => (o === "" ? null : v))
              .required("Please provide interval."),
            days: yup
              .array()
              .of(yup.string())
              .when("base", ([base]) => {
                if (base !== "weeks") return yup.mixed().nullable();
                return yup
                  .array()
                  .of(yup.string())
                  .test({
                    test: v => Array.isArray(v) && v.length > 0,
                    message: "Please provide weekday."
                  });
              })
          });

        return s;
      })
  });

  const form = useForm({
    defaultValues: {
      name: "",
      ...DEFAULT_FREQUENCY
    },
    resolver: yupResolver(schema)
  });

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: {errors}
  } = form;

  const hydrateForm = useCallback(
    values => {
      hydratingFrequencyRef.current = {
        frequency: values?.frequency ?? "",
        base: values?.recurrence?.base ?? "days",
        startDate: values?.start?.date ?? null
      };
      reset(values);
    },
    [reset]
  );

  const defaultName = watch("name") ?? "";
  const watchFrequency = watch("frequency");
  const allowExternal = canSetExternalChecksheet(watchFrequency);

  useEffect(() => {
    if (!allowExternal) setExternal(false);
  }, [allowExternal]);

  const currentPreviewFrequency = useMemo(() => {
    const freq = expandNames ? frequency?.frequency : watchFrequency;
    if (!freq || freq === "none") return null;
    return freq;
  }, [expandNames, frequency, watchFrequency]);

  const stateVariantName = useMemo(
    () => (stateId ? states[stateId]?.name ?? "" : ""),
    [stateId, states]
  );

  const currentName = useMemo(() => {
    const base = defaultName.trim();
    if (currentStateId === DEFAULT_STATE.id) return base;
    const custom = stateVariantName.trim();
    return custom || base;
  }, [currentStateId, defaultName, stateVariantName]);

  const frequencySelectOptions = useMemo(
    () => [
      {name: "none", label: "None"},
      ...(frequencies || []),
      {name: "custom", label: "Custom..."}
    ],
    [frequencies]
  );

  const handleFrequencySelectChange = useCallback(
    (targetStateId, frequencyValue) => {
      if (`${targetStateId}` === `${currentStateId}`) {
        setValue("frequency", frequencyValue);
        return;
      }

      setStates(prev => {
        const temp = {
          ...prev,
          [targetStateId]: {
            ...prev[targetStateId],
            frequency: {...prev[targetStateId].frequency, frequency: frequencyValue}
          }
        };
        persistStates(temp);
        return temp;
      });
      setCurrentStateId(`${targetStateId}`);
    },
    [currentStateId, setValue, persistStates]
  );

  const renderStateFrequencySelect = (targetStateId, testId) => (
    <StateFrequencySelect
      fullWidth
      value={states[targetStateId]?.frequency?.frequency ?? ""}
      onChange={e => handleFrequencySelectChange(targetStateId, e.target.value)}
      data-testid={testId}
      required>
      <option value="" disabled>
        Frequency...
      </option>
      {frequencySelectOptions.map(({name, label}) => (
        <option key={name} value={name}>
          {label}
        </option>
      ))}
    </StateFrequencySelect>
  );

  const watchStartDate = watch("start.date");
  const watchVisibleDate = watch("start.visibleDate");
  const watchLastDay = watch("start.lastDay");
  const watchDueDays = watch("recurrence.days");
  const watchBase = watch("recurrence.base");
  const watchInterval = watch("recurrence.interval");
  const watchAvailability = watch("recurrence.availability");

  const watchedFreqObj = useMemo(
    () => ({
      frequency: watchFrequency,
      start: {
        date: watchStartDate,
        visibleDate: watchVisibleDate,
        lastDay: watchLastDay
      },
      recurrence: {
        days: watchDueDays,
        base: watchBase,
        interval: watchInterval,
        availability: watchAvailability
      }
    }),
    [
      watchAvailability,
      watchBase,
      watchDueDays,
      watchFrequency,
      watchInterval,
      watchLastDay,
      watchStartDate,
      watchVisibleDate
    ]
  );

  const prevFrequency = usePrevious(frequency);
  const prevFreqObj = usePrevious(watchedFreqObj);

  const getKeysChanged = useCallback((obj1, obj2, changed = []) => {
    if (!obj1 || !obj2) return changed;
    const keys1 = Object.keys(obj1);
    for (let i = 0; i < keys1.length; i++) {
      const key = keys1[i];
      const val1 = obj1[key];
      const val2 = obj2[key];
      if (typeof val1 !== typeof val2) changed.push(key);
      else if (typeof val1 === "object") getKeysChanged(val1, val2, changed);
      else if (val1 !== val2) changed.push(key);
    }
    return changed;
  }, []);

  const handleCollapseNames = useCallback(() => {
    setStates(prev => {
      const sharedFreq = {...watchedFreqObj};
      const temp = Object.fromEntries(
        Object.keys(prev).map(id => [id, {...prev[id], frequency: {...sharedFreq}}])
      );
      persistStates(temp);
      return temp;
    });
    setExpandNames(false);
  }, [watchedFreqObj, persistStates]);

  const renderNameField = () => {
    const defaultNameInput = (
      <InputText name="name" placeholder="Name" testId="checksheet.name" required />
    );

    // No state variant configured -> single shared name.
    if (!stateId)
      return (
        <NameRow>
          <NameInputWrapper>{defaultNameInput}</NameInputWrapper>
        </NameRow>
      );

    // Collapsed -> name and frequency stacked, expand control on the right.
    if (!expandNames)
      return (
        <NameRowCollapsed>
          <NameFieldStack>
            {defaultNameInput}
            {frequencies?.length > 0 && (
              <InputSelect
                name="frequency"
                label="Frequency"
                help="How often this checksheet is due. Choosing a frequency reveals the dates below, showing when the checksheet becomes available and when it is next due."
                placeholder="Frequency..."
                testId="checksheet.frequency"
                fullWidth
                options={frequencySelectOptions}
                required
              />
            )}
          </NameFieldStack>
          <NameToggleButton
            type="button"
            onClick={() => setExpandNames(true)}
            title="Set a different name or frequency per state"
            data-testid="checksheet.expandNames">
            <FontAwesomeIcon icon={faPlus} />
          </NameToggleButton>
        </NameRowCollapsed>
      );

    // Expanded -> separate DEFAULT and state-specific name + frequency inputs.
    return (
      <NameGroupExpanded>
        <NamePill>{DEFAULT_STATE.name}</NamePill>
        <NameFieldStack>
          {defaultNameInput}
          {frequencies?.length > 0 &&
            renderStateFrequencySelect(DEFAULT_STATE.id, "checksheet.frequency.default")}
        </NameFieldStack>
        <NameToggleButton
          type="button"
          onClick={handleCollapseNames}
          title="Use one name and frequency for all states"
          data-testid="checksheet.collapseNames">
          <FontAwesomeIcon icon={faMinus} />
        </NameToggleButton>
        <NamePill color={state?.color ? `#${state.color}` : undefined}>
          {state?.name || "STATE"}
        </NamePill>
        <NameFieldStack>
          <Input
            type="text"
            data-testid="checksheet.stateName"
            placeholder={defaultName.trim() || "Name"}
            value={states[stateId]?.name ?? ""}
            onChange={e => handleStateNameChange(e.target.value)}
          />
          {frequencies?.length > 0 &&
            renderStateFrequencySelect(stateId, "checksheet.frequency.state")}
        </NameFieldStack>
      </NameGroupExpanded>
    );
  };

  const initialized = useRef(false);
  const prevCurrentStateId = usePrevious(currentStateId);

  useEffect(() => {
    if (initialized.current) {
      // either frequency changed, or active checksheet state changed
      const keysDifferent = getKeysChanged(watchedFreqObj, frequency);
      if (keysDifferent.length) {
        // explicitly checks for change in form state object
        const formChanged = getKeysChanged(watchedFreqObj, prevFreqObj);
        const stateChanged = getKeysChanged(frequency, prevFrequency);
        const switchedState = prevCurrentStateId !== currentStateId;

        // Hydrate the form from state only when switching the active checksheet state.
        // Otherwise prefer the form as source of truth — weekday/start sync and setStates
        // often land in the same render, and resetting here caused max update depth with
        // per-state names expanded.
        if (switchedState && stateChanged.length && expandNames) {
          hydrateForm({
            name: defaultName,
            ...frequency
          });
        } else if (formChanged.length) {
          setStates(prev => {
            const freqUpdate = {...watchedFreqObj};
            const temp = expandNames
              ? {...prev, [currentStateId]: {...prev[currentStateId], frequency: freqUpdate}}
              : Object.fromEntries(
                  Object.keys(prev).map(id => [id, {...prev[id], frequency: {...freqUpdate}}])
                );
            persistStates(temp);
            return temp;
          });
        }
      }
    }
    initialized.current = true;
  }, [
    setValue,
    currentStateId,
    frequency,
    watchedFreqObj,
    prevFreqObj,
    persistStates,
    getKeysChanged,
    hydrateForm,
    defaultName,
    prevFrequency,
    expandNames,
    prevCurrentStateId
  ]);

  useEffect(() => {
    if (!expandNames && initialized.current && prevCurrentStateId !== currentStateId) {
      setStates(prev => {
        const sharedFreq = {...watchedFreqObj};
        const temp = Object.fromEntries(
          Object.keys(prev).map(id => [id, {...prev[id], frequency: {...sharedFreq}}])
        );
        persistStates(temp);
        return temp;
      });
    }
  }, [currentStateId, expandNames, watchedFreqObj, prevCurrentStateId, persistStates]);

  const filterCount = useMemo(() => {
    let count = 0;
    if (filters?.types?.length) count += filters.types.length;
    if (filters?.tags?.length) count += filters.tags.length;
    return count;
  }, [filters]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (!showActionsMenu) return;
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActionsMenu]);

  const getTemplateLabel = id => {
    const filtered = templateList.filter(t => t.id === id);
    if (filtered.length) return filtered[0].label;
    return "Template";
  };

  const getFacility = useCallback(
    () =>
      apiFacility.callGet().then(result => {
        if (result && result.status === 200 && result.data) setFacility(result.data);
      }),
    [apiFacility, setFacility]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted()) {
      apiFrequency.callGet().then(result => {
        if (result && result.status === 200 && result.data) setFrequencies(result.data);
      });
      apiUnits.callGet().then(result => {
        if (result && result.status === 200 && result.data) setUnits(result.data);
      });

      if (facility === null) getFacility();
    }
  }, [isMounted, apiFrequency, apiUnits, facility, getFacility]);

  // Set preview based on selected frequency
  useEffect(() => {
    if (watchFrequency !== "none" && watchStartDate) {
      let params;

      if (watchFrequency !== "custom") params = {start: watchStartDate, name: watchFrequency};

      if (watchFrequency === "custom" && watchInterval && watchBase) {
        params = {
          start: watchStartDate,
          interval: Number.parseInt(watchInterval, 10),
          base: watchBase,
          exclude:
            watchDueDays && watchDueDays.length > 1
              ? DAY_IDX.filter(idx => !watchDueDays.includes(IDX_TO_DAY[idx]))
              : null
        };
        if (watchAvailability) {
          params.availableBase =
            watchBase.includes("month") || watchInterval === 1 ? "months" : "years";
          params.availableIndex = parseInt(watchAvailability, 10);
        }
      }

      if (params)
        apiFrequency.callGet(null, params).then(({status, data}) => {
          if (status === 200 && data) setStartPreview(data);
        });
    }
  }, [
    apiFrequency,
    watchStartDate,
    watchLastDay,
    watchFrequency,
    watchInterval,
    watchBase,
    watchDueDays,
    watchAvailability
  ]);

  const hasLastDayOption = useMemo(() => {
    if (!watchFrequency) return false;
    if (watchFrequency === "custom")
      return watchBase?.includes("month") || watchBase?.includes("year");
    return !NO_LAST_DAY_OPTION.includes(watchFrequency);
  }, [watchBase, watchFrequency]);

  const availableOptions = useMemo(() => {
    if (
      watchFrequency !== CUSTOM_OPTION.name ||
      !["month", "months", "year", "years"].includes(watchBase)
    )
      return [];

    const options = [];

    const parsedInterval = parseInt(watchInterval, 10);

    if (exists(parsedInterval) && !Number.isNaN(parsedInterval)) {
      if (watchBase?.includes("month") && parsedInterval > 1) {
        for (let i = 1; i < parsedInterval + 1; i++) {
          options.push({name: i, label: `${i}${getNumberSuffix(i)} Month`});
        }
      }

      if (watchBase?.includes("year")) {
        if (parsedInterval === 1) {
          for (let i = 1; i < 13; i++) {
            options.push({name: i, label: `${i}${getNumberSuffix(i)} Month`});
          }
        } else {
          for (let i = 1; i < parsedInterval + 1; i++) {
            options.push({name: i, label: `${i}${getNumberSuffix(i)} Year`});
          }
        }
      }
    }

    return options;
  }, [watchFrequency, watchBase, watchInterval]);

  const showAvailable = useCallback(freq => {
    if (freq.frequency !== CUSTOM_OPTION.name) return false;
    if (freq.recurrence.base?.includes("month"))
      return (
        freq.recurrence.interval && freq.recurrence.interval > 1 && freq.recurrence.interval < 24
      );
    if (freq.recurrence.base?.includes("year"))
      return freq.recurrence.interval && freq.recurrence.interval < 10;
    return false;
  }, []);

  const frequencyWarning = useMemo(() => {
    if (
      (watchFrequency === "monthly" || watchFrequency === "bi-monthly" || watchBase === "months") &&
      dayjs(watchStartDate).date() > 28
    )
      return `For months with less or greater than 
          ${dayjs(watchStartDate).date()} days, this checksheet will be due on the last day of the
          month`;

    if (watchFrequency === "semi-monthly")
      return "Due date defaults to the middle or last day of the month for semi-monthly tasks.";

    if (watchFrequency === "quarterly")
      return "Due date defaults to the last day of the quarter for quarterly tasks.";

    if (watchFrequency === "quadrimesterly")
      return "Start date defaults to the first day of the year and is due every four months.";

    if (watchFrequency === "semi-annually")
      return "Start date defaults to the first day of the year and is due every six months.";

    if (watchFrequency === CUSTOM_OPTION.name && watchBase?.includes("month"))
      if (watchInterval <= 1)
        return "Availability window not supported for intervals of less than 2 months.";
      else if (watchInterval >= 24)
        return "Availability window not supported for intervals of 24 months or longer.";

    if (watchFrequency === CUSTOM_OPTION.name && watchBase?.includes("year"))
      if (watchInterval > 10)
        return "Availability window not supported for intervals of longer than 10 years.";

    return "";
  }, [watchFrequency, watchBase, watchStartDate, watchInterval]);

  const toggledElements = updated => {
    for (let i = 0; i < updated.allIds.length; i++) {
      if (updated.byId[updated.allIds[i]].toggle) return true;
    }
    return false;
  };

  const saveBuilder = useCallback(
    (updated, newUnits, templates, previousById, stateIdToUse = currentStateId) => {
      setError(null);
      setRefreshMissing(true);
      // Maintain list of templates that have been applied
      const templatesAppliedToState = appliedTemplates?.[stateIdToUse]
        ? {...appliedTemplates[stateIdToUse]}
        : {};
      if (templates && Object.keys(templates).length > 0 && previousById) {
        const newTemplate = Object.keys(templates)[0];
        const components = templates[newTemplate];
        // Link if template is applied or component is manually linked
        if (components && newTemplate && newTemplate !== "unlink") {
          components.forEach(component => {
            const oldTemplate =
              component in previousById ? previousById[component].templateId : null;

            // Template link needs to be overrided in favor of new link
            if (
              oldTemplate &&
              oldTemplate !== newTemplate &&
              oldTemplate in templatesAppliedToState
            ) {
              const temp = templatesAppliedToState[oldTemplate].filter(c => c !== component);
              if (temp.length > 0) templatesAppliedToState[oldTemplate] = temp;
              else delete templatesAppliedToState[oldTemplate];
            }

            // Inlitialize/Add component list on Template
            if (
              newTemplate in templatesAppliedToState &&
              !(component in templatesAppliedToState[newTemplate])
            )
              templatesAppliedToState[newTemplate].push(component);
            else templatesAppliedToState[newTemplate] = [component];
          });
        }

        // Unlink if user manually unlinks a template
        if (components && newTemplate && newTemplate === "unlink")
          components.forEach(component => {
            const oldTemplate =
              component in previousById ? previousById[component].templateId : null;
            if (
              oldTemplate &&
              oldTemplate !== newTemplate &&
              oldTemplate in templatesAppliedToState
            ) {
              const temp = templatesAppliedToState[oldTemplate].filter(c => c !== component);
              if (temp.length > 0) templatesAppliedToState[oldTemplate] = temp;
              else delete templatesAppliedToState[oldTemplate];
            }
          });
      }

      if (newUnits && newUnits.length > 0) {
        const temp = newUnits.filter(unit => !unitsToAdd.includes(unit));
        setUnitsToAdd(prev => [...prev, ...temp]);
      }

      setHasToggledOn(!!toggledElements(updated));

      if (templates)
        setAppliedTemplates(prev => ({...prev, [stateIdToUse]: templatesAppliedToState}));

      setStates(prev => {
        const updatedStates = Object.fromEntries(
          Object.keys(prev || {}).map(id => {
            if (id === stateIdToUse) {
              // Fully override with the new builder for current state
              return [id, {...prev[id], builder: {...updated}}];
            }
            // Otherwise, just update existing elements in byId (to keep elements found in both in sync)
            const v = prev[id];
            const updatedElements = Object.fromEntries(
              Object.keys(v.builder.byId).map(name => [
                name,
                name in updated.byId
                  ? {
                      ...updated.byId[name],
                      toggle: v.builder.byId[name].toggle,
                      children: v.builder.byId[name].children,
                      parentName: v.builder.byId[name].parentName
                    }
                  : v.builder.byId[name]
              ])
            );
            return [
              id,
              {
                ...v,
                builder: {
                  ...v.builder,
                  byId: {
                    ...v.builder.byId,
                    ...updatedElements
                  }
                }
              }
            ];
          })
        );

        persistStates(updatedStates);
        return updatedStates;
      });
    },
    [appliedTemplates, unitsToAdd, currentStateId, persistStates]
  );

  const saveNotifications = useCallback(
    notifications => {
      if (canPersistRef.current) persistTempChecksheet(slug, "notifications", notifications);
      setFieldNotifications(notifications);
    },
    [slug]
  );

  // Restore in-progress builder immediately on mount (do not wait for facility —
  // other effects can otherwise persist an empty builder over the cache).
  useEffect(() => {
    if (!slug) return;
    if (retrievedLocalStorage.current === slug) return;

    retrievedLocalStorage.current = slug;

    const storedDraft = readTempChecksheet(slug, "draft");
    const storedStates =
      readTempChecksheet(slug, "states") ||
      (storedDraft?.states && Object.keys(storedDraft.states).length > 0
        ? storedDraft.states
        : null);
    const storedNotifications = readTempChecksheet(slug, "notifications");

    const updatedStates =
      storedStates && Object.keys(storedStates).length > 0 ? storedStates : null;

    if (updatedStates) {
      const defaultBuilder = updatedStates[DEFAULT_STATE.id]?.builder;
      setStates(updatedStates);
      if (defaultBuilder?.allIds?.length > 0) {
        setHasToggledOff(getToggleState(defaultBuilder.allIds, defaultBuilder));
        setHasToggledOn(defaultBuilder.allIds.some(id => defaultBuilder.byId?.[id]?.toggle));
      }
    }

    const restoredFrequency =
      storedDraft?.frequency || updatedStates?.[DEFAULT_STATE.id]?.frequency;
    if (storedDraft?.name || restoredFrequency) {
      hydrateForm({
        name: storedDraft?.name ?? "",
        ...DEFAULT_FREQUENCY,
        ...(restoredFrequency || {})
      });
    }

    if (storedDraft?.currentStateId) setCurrentStateId(`${storedDraft.currentStateId}`);
    if (storedDraft?.expandNames) setExpandNames(true);
    if (storedDraft?.restrictTo) setRestrictTo(storedDraft.restrictTo);
    if (typeof storedDraft?.external === "boolean") setExternal(storedDraft.external);
    if (storedDraft?.checksheetRestrictions) {
      setChecksheetRestrictions(storedDraft.checksheetRestrictions);
    }
    if (storedDraft?.appliedTemplates) setAppliedTemplates(storedDraft.appliedTemplates);

    if (storedNotifications?.allIds?.length > 0) saveNotifications(storedNotifications);

    canPersistRef.current = true;
    setDraftReady(true);
  }, [slug, hydrateForm, saveNotifications]);

  useEffect(() => {
    if (!draftReady || !slug || !canPersistRef.current) return;
    persistTempChecksheet(slug, "draft", {
      name: defaultName,
      frequency: watchedFreqObj,
      currentStateId,
      expandNames,
      restrictTo,
      external,
      checksheetRestrictions,
      appliedTemplates,
      states
    });
    persistTempChecksheet(slug, "states", states);
  }, [
    draftReady,
    slug,
    defaultName,
    watchedFreqObj,
    currentStateId,
    expandNames,
    restrictTo,
    external,
    checksheetRestrictions,
    appliedTemplates,
    states
  ]);

  const hasUnsavedWork = useMemo(() => {
    if (!draftReady) return false;
    return (
      Object.values(states).some(s => s?.builder?.allIds?.length > 0) ||
      !!defaultName.trim() ||
      !!watchFrequency ||
      restrictTo?.length > 0 ||
      fieldNotifications?.allIds?.length > 0 ||
      checksheetRestrictions?.length > 0 ||
      Object.keys(states).length > 1
    );
  }, [
    draftReady,
    states,
    defaultName,
    watchFrequency,
    restrictTo,
    fieldNotifications,
    checksheetRestrictions
  ]);

  const promptForSave = useCallback(action => {
    setQueuedAction(() => action);
    setLeaveWarning(true);
  }, []);

  useEffect(() => {
    if (hasUnsavedWork) setIntermediateAction(() => promptForSave);
    else setIntermediateAction(undefined);
    return () => setIntermediateAction(undefined);
  }, [hasUnsavedWork, promptForSave, setIntermediateAction]);

  const runQueuedAction = () => {
    if (typeof queuedAction === "string") navigate(queuedAction);
    else if (typeof queuedAction === "function") queuedAction();
    setQueuedAction(undefined);
    setLeaveWarning(false);
  };

  const handleCacheAndLeave = () => runQueuedAction();

  const handleDiscardAndLeave = () => {
    canPersistRef.current = false;
    clearTempChecksheet(slug);
    runQueuedAction();
  };

  // Handle filtering
  useEffect(() => {
    const {byId} = builder;

    if (filters?.types || filters?.tags)
      setFilterResult(
        Object.fromEntries(
          Object.entries(byId).filter(
            ([, {tag, type}]) =>
              filters.types?.includes(type) || (tag && filters.tags?.includes(tag))
          )
        )
      );
    else {
      setFilters(null);
      setFilterResult(null);
    }
  }, [filters, builder]);

  const handleClear = () => {
    setFilters(null);
    setFilterResult(null);
    setShowFilterModal(false);
  };

  const onDragStart = provided => {
    const {droppableId} = provided.source;
    setActiveDroppableId(droppableId);
  };

  // re-ordering list
  const onDragEnd = result => {
    const {destination, source, type} = result;

    if (!destination) return;

    let updatedBuilder;
    const builderCopy = {...builder};

    if (type === "DEFAULT") {
      const allIds = [...builder.allIds];

      const [removed] = allIds.splice(source.index, 1);
      allIds.splice(destination.index, 0, removed);

      updatedBuilder = {...builderCopy, allIds};

      saveBuilder(updatedBuilder);
    }

    if (type === source.droppableId) {
      const byId = {...builder.byId};

      const parent = byId[type];
      const {children} = parent;

      const [removed] = children.splice(source.index, 1);
      children.splice(destination.index, 0, removed);

      const help = sortHelp(parent.help, children, byId);

      updatedBuilder = {...builderCopy, byId: {...byId, [type]: {...parent, children, help}}};

      saveBuilder(updatedBuilder);
    }
  };

  const recursePreview = (elementName, updatedById, inheritPreview = null) => {
    if (updatedById[elementName] === undefined) return updatedById;

    const node = updatedById[elementName];
    const nextPreview = inheritPreview !== null ? inheritPreview : !node.preview;

    if (inheritPreview !== null || node.toggle || node.element === FIELD) {
      updatedById[elementName].preview = nextPreview;
    }

    node.children?.forEach(child => {
      updatedById = recursePreview(child, updatedById, nextPreview);
    });

    return updatedById;
  };

  const togglePreview = elementName => {
    const updatedById = recursePreview(elementName, {...builder.byId});
    setStates(prev => ({
      ...prev,
      [currentStateId]: {
        ...prev[currentStateId],
        builder: {...builder, byId: {...updatedById}}
      }
    }));
  };

  const addTemplate = (componentName, templateLabel) => {
    const component = builder.byId[componentName];
    const fields = builder.byId[componentName].children.map(child => ({...builder.byId[child]}));
    const nameMap = {};
    const tempBuilder = {byId: {}};
    const extern = {};
    let extIdx = 0;

    // iterate over each field to add it to the temporary builder.
    // this is for resolving mismatched dependencies due to the template
    // being removed from the context of the current builder
    fields.forEach(field => {
      delete field.preview;
      delete field.toggle;
      delete field.shared;
      if ("linkedGroup" in field) delete field.linkedGroup;
      if ("linkedComponent" in field) delete field.linkedComponent;
      const name = generateUniqueKey(field.label);
      nameMap[field.name] = name;
      nameMap[componentName] = "component";
      field.name = name;

      // if a field has a formula, iterate over each part to set up
      // nameMap and the temporary builder (for resolving dependencies)
      if (field.formula)
        field.formula
          .filter(({value}) => value in builder.byId)
          .map(({value}) => {
            const item = builder.byId[value];
            // any items in formula that fall outside component
            // are reassigned to name ext_ where _ represents a unique integer
            if (item.parentName !== componentName) {
              let key = nameMap[value];
              // if field name already exists in nameMap, use that mapping instead of
              // generating a new ext_ key
              if (!key) {
                nameMap[value] = `ext${extIdx}`;
                key = `ext${extIdx}`;
                extIdx++;
              }
              if (!(key in extern)) {
                tempBuilder.byId[key] = {
                  ...item,
                  name: key,
                  formula: undefined,
                  condition: undefined
                };
                extern[key] = {
                  label: item.label,
                  type: item.type
                };
              }
            }
          });

      // if a field has conditions, iterate over each condition in list to set up
      // nameMap and the temporary builder (for resolving dependencies)
      if (field.condition && field.condition.list)
        field.condition.list
          .filter(({depends}) => depends in builder.byId)
          .map(({depends}) => {
            const item = builder.byId[depends];

            // any items in condition that fall outside component
            // are reassigned to name ext_ where _ represents a unique integer
            if (item.parentName !== componentName && depends !== componentName) {
              let key = nameMap[depends];
              // if field name already exists in nameMap, use that mapping instead of
              // generating a new ext_ key
              if (!key) {
                nameMap[depends] = `ext${extIdx}`;
                key = `ext${extIdx}`;
                extIdx++;
              }

              if (!(key in extern)) {
                tempBuilder.byId[key] = {
                  ...item,
                  name: key,
                  formula: undefined,
                  condition: undefined
                };
                extern[key] = {
                  label: item.label,
                  type: item.type
                };
              }
            }
          });

      tempBuilder.byId[field.name] = {
        ...field,
        formula: field.formula ? [...field.formula.map(part => ({...part}))] : null,
        condition: field.condition
          ? {...field.condition, list: [...field.condition.list.map(item => ({...item}))]}
          : null
      };
    });

    resolveDependencyMismatches({byId: {...builder.byId, ...tempBuilder.byId}}, nameMap);
    const templateToAdd = {
      label: templateLabel,
      options: {
        grid: component.grid,
        help: component?.help?.map(item => ({
          ...item,
          id: item.id in nameMap ? nameMap[item.id] : ""
        })),
        external: extern
      },
      fields: Object.values(tempBuilder.byId).filter(({name}) => !name.match("^ext[0-9]*$"))
    };
    apiBase.callPost({...templateToAdd}).then(() => {
      setRefreshTemplates(true);
      setTarget(null);
    });
  };

  const handleDeleteState = () => {
    setStates(prev => {
      const temp = {};
      temp[DEFAULT_STATE.id] = prev[DEFAULT_STATE.id];
      persistStates(temp);
      return temp;
    });
    setCurrentStateId(DEFAULT_STATE.id);
    setShowModalConfirmDeleteState(false);
    addToast("State successfully deleted", "success");
  };

  const handleEdit = (update, updatedBuilder) => {
    const currentBuilder = updatedBuilder ?? builder;
    const newUnits = [];
    if (update.units && !units.includes(update.units)) {
      setUnits(prev => [...prev, update.units].sort());
      newUnits.push(update.units);
    }
    if (update.type !== builder.byId[update.name].type) handleClear();
    const {builder: updated, notifications: updatedNotifications} = updateElement(
      currentBuilder,
      update,
      update.element === "field",
      undefined,
      false,
      false,
      fieldNotifications
    );
    saveBuilder(
      updated,
      newUnits,
      update?.templateId ? {unlink: [update.name]} : null,
      currentBuilder.byId
    );
    saveNotifications(updatedNotifications);
    setShowModalEdit(false);
  };

  const handleDelete = (element, notificationsToDelete) => {
    // Handle builder element removal
    const updatedBuilder = removeElement(builder, element);
    saveBuilder(updatedBuilder);

    // Handle notification delete
    if (notificationsToDelete && notificationsToDelete.length > 0) {
      const updatedNotifications = {...fieldNotifications};
      notificationsToDelete.map(id => {
        if (Object.keys(updatedNotifications.byId).includes(id) && updatedNotifications.byId[id])
          // Delete field notification object
          delete updatedNotifications.byId[id];

        const temp = updatedNotifications.allIds.indexOf(id);
        updatedNotifications.allIds.splice(temp, 1);
      });
      saveNotifications(updatedNotifications);
    }

    setShowModalDelete(false);
  };

  const handleAddField = (field, updatedBuilder) => {
    const currentBuilder = updatedBuilder ?? builder;
    handleClear();
    const newUnits = [];
    if (field.units && !units.includes(field.units)) {
      setUnits(prev => [...prev, field.units].sort());
      newUnits.push(field.units);
    }
    const temp = {...field, toggle: true};
    const updated = addField(currentBuilder, temp);
    saveBuilder(updated, newUnits);
  };

  const handleAddMultipleFields = fields => {
    handleClear();
    const newUnits = [];
    let updated = {...builder};
    fields.map(field => {
      if (field.units && !units.includes(field.units)) {
        setUnits(prev => [...prev, field.units].sort());
        newUnits.push(field.units);
      }
      const temp = {...field, toggle: true};
      updated = addField(updated, temp);
    });
    saveBuilder(updated, newUnits);
  };

  const handleFormula = (field, updatedBuilder) => {
    if (field.type === "generated") handleEdit(field, updatedBuilder);
    else
      handleAddField(
        {
          ...field,
          type: "generated",
          disabled: true
        },
        updatedBuilder
      );
  };

  const handleConditional = values => {
    const {parentName} = target;
    const update = {
      ...target,
      condition: values.condition
    };
    if (parentName) {
      const parentElement = builder.byId[parentName];
      const parentHelp = parentElement.help;
      const helpItem = parentHelp?.find(({id}) => id === target.name);
      if (helpItem?.value) update.help = helpItem.value;
    }
    handleEdit(update);
  };

  const cloneElement = targetElement => {
    const {element, label, name, parentName} = targetElement;

    const newLabel = `${label} COPY`;
    if (labelAlreadyExists(newLabel, name, parentName, builder)) {
      const parentLabel = parentName ? builder.byId[parentName]?.label : null;
      addToast(
        parentLabel
          ? `${newLabel} already exists in ${parentLabel}`
          : `${newLabel} already exists on top level`,
        "error"
      );
    } else {
      const parent = builder.byId[parentName];
      let foundHelp;
      if (parent?.help && element === FIELD) {
        foundHelp = parent.help.find(({id}) => id === name);
      }
      const clone = {
        ...builder.byId[name],
        label: `${label} COPY`,
        name: null,
        toggle: true,
        condition: null,
        help: foundHelp?.value
      };
      const nameMap = {};

      let updated;
      if (element === "field") updated = addField(builder, clone);
      if (element === "component") updated = addComponent(builder, clone, nameMap);
      if (element === "group") {
        const cloneIdx = builder.allIds.indexOf(name) + 1;
        updated = addGroup(builder, {...clone, cloneIdx}, nameMap);
      }

      resolveDependencyMismatches(updated, nameMap);

      saveBuilder(updated);
    }
  };

  const getFreqAttributes = freq => {
    const attr = {};
    if (freq.frequency === "custom") {
      attr.interval = Number.parseInt(freq.recurrence.interval, 10);
      attr.base = freq.recurrence.base;

      if (freq.recurrence.days?.length > 1)
        attr.exclude = DAY_IDX.filter(idx => !freq.recurrence.days.includes(IDX_TO_DAY[idx]));

      if (showAvailable(freq) && attr.base && freq.recurrence.availability) {
        attr.availableBase =
          attr.base.includes("month") || attr.interval === 1 ? "months" : "years";
        attr.availableIndex = parseInt(freq.recurrence.availability, 10);
      }
    }

    if (freq.frequency !== "none") {
      attr.startDate = freq.start.date;
      attr.lastDay = freq.start.lastDay;

      if (freq.frequency !== "custom") attr.frequency = freq.frequency;
    }

    return attr;
  };

  const handlePublish = (draft = false) => {
    draft = draft === true;
    // validate builder structure
    const defaultInfo = states[DEFAULT_STATE.id];
    const stateInfo = stateId ? states[stateId] : {};

    const {builder: defaultBuilder, frequency: defaultFrequency} = defaultInfo;
    const {builder: stateBuilder, frequency: stateFrequency} = stateInfo;

    const {allIds} = defaultBuilder || {allIds: []};
    const defaultChecksheetBuilder = newFromBuilder(
      allIds,
      {...defaultBuilder},
      {allIds: [], byId: {}},
      true
    );

    const stateChecksheetBuilder = stateBuilder
      ? newFromBuilder(stateBuilder.allIds, {...stateBuilder}, {allIds: [], byId: {}}, true)
      : null;

    const defaultError = !validBuilder(defaultChecksheetBuilder.allIds, defaultChecksheetBuilder);
    const stateError = stateChecksheetBuilder
      ? !validBuilder(stateChecksheetBuilder.allIds, stateChecksheetBuilder)
      : undefined;

    const newError = {};

    if (defaultError) newError[DEFAULT_STATE.id] = true;
    if (stateError) newError[stateId] = true;

    const errorState = Object.values(newError).some(val => val);

    setError(errorState ? newError : null);

    if (errorState) {
      if (stateError && currentStateId === DEFAULT_STATE.id) {
        setCurrentStateId(stateId);
      } else if (defaultError && currentStateId !== DEFAULT_STATE.id) {
        setCurrentStateId(DEFAULT_STATE.id);
      }
    } else {
      let checksheet = {
        facilityId: facility.id,
        name: defaultName.trim(),
        builder: {...defaultChecksheetBuilder},
        templates: appliedTemplates,
        notifications: {...fieldNotifications},
        external: allowExternal && external,
        checksheetRestrictions
      };

      if (stateChecksheetBuilder) {
        const facilityStateId = Number(stateId);
        if (!Number.isFinite(facilityStateId)) {
          addToast(
            "Cannot publish checksheet state: facility state id is missing or invalid.",
            "error"
          );
          return;
        }
        const trimmedStateName = stateVariantName.trim();
        checksheet.state = {
          builderOverrides: getBuilderOverrides(stateChecksheetBuilder, defaultChecksheetBuilder),
          facilityStateId,
          ...(trimmedStateName ? {name: trimmedStateName} : {})
        };
      }

      if (defaultFrequency) checksheet = {...checksheet, ...getFreqAttributes(defaultFrequency)};
      if (stateFrequency)
        checksheet = {
          ...checksheet,
          state: {
            ...checksheet.state,
            ...getFreqAttributes(expandNames ? stateFrequency : defaultFrequency)
          }
        };

      if (draft === true) setLoadingDraft(true);
      else setLoadingPublish(true);

      if (unitsToAdd && unitsToAdd.length > 0) checksheet.units = unitsToAdd;

      checksheet.draft = draft;

      if (restrictTo?.length > 0) {
        if (restrictTo[0]?.publicId)
          checksheet.restrictToUser = restrictTo.map(({publicId}) => publicId);
        else checksheet.restrictToRole = restrictTo.map(({id}) => id);
      }

      apiChecksheet
        .callPost({...checksheet})
        .then(({status}) => {
          if (status === 201) {
            // Suppress draft rewrite while we clear cache and reset form state.
            canPersistRef.current = false;
            clearTempChecksheet(slug);
            getNotifications(slug);
            // reset states
            reset({
              name: "",
              type: "",
              role: "",
              draft: false,
              ...DEFAULT_FREQUENCY
            });
            setHasToggledOff(true);
            setStates({0: {builder: {allIds: [], byId: {}}, frequency: {...DEFAULT_FREQUENCY}}});
            setCurrentStateId(DEFAULT_STATE.id);
            setFieldNotifications({allIds: [], byId: {}});
            setUnitsToAdd([]);
            setAppliedTemplates({});
            setRestrictTo([]);
            setExternal(false);
            setChecksheetRestrictions([]);
            // Re-enable after the empty reset has flushed so later edits can cache again.
            setTimeout(() => {
              canPersistRef.current = true;
            }, 0);
          }
        })
        .finally(() => {
          setLoadingPublish(false);
          setLoadingDraft(false);
        });
    }
  };

  const toggleAll = () => {
    if (builder && builder.allIds && builder.allIds.length > 0) {
      setLoadingToggleAll(true);
      const updatedBuilder = recurseToggle(builder.allIds, builder, hasToggledOff);
      setHasToggledOff(!hasToggledOff);
      saveBuilder({...updatedBuilder});
      setLoadingToggleAll(false);
    }
  };

  const handleUseFacilityBuilder = () => {
    if (hasElements) {
      setHasToggledOff(true);
      saveBuilder({allIds: [], byId: {}});
    } else if (facility?.builder) {
      setHasToggledOff(!!facility.builder.allIds && facility.builder.allIds.length > 0);
      saveBuilder({
        byId: {
          ...Object.fromEntries(
            Object.entries(facility.builder.byId).map(([key, val]) => [
              key,
              {
                ...val,
                children: val.children ? [...val.children] : val.children
              }
            ])
          )
        },
        allIds: [...facility.builder.allIds]
      });
    }
  };

  const defaultDate = useMemo(() => {
    let date = dayjs();
    let lastDay = false;
    if (["weekly", "bi-weekly"].includes(watchFrequency) || watchBase === "weeks")
      date = date.endOf("week").add(1, "day");
    if (["monthly", "bi-monthly"].includes(watchFrequency) || watchBase === "months") {
      lastDay = true;
      date = date.endOf("month");
    }
    if (["annually", "biennially", "triennially"].includes(watchFrequency) || watchBase === "years")
      date = date.endOf("year");

    if (watchFrequency === "semi-monthly") {
      const midCurrentMonth = Math.floor(date.daysInMonth() / 2);

      if (date.date() > midCurrentMonth) {
        date = date.date(date.daysInMonth());
      } else {
        date = date.date(midCurrentMonth);
      }
    }

    if (watchFrequency === "quarterly") {
      const currentMonth = date.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 3) nextMonth = 2;
      else if (nextMonth >= 3 && nextMonth < 6) nextMonth = 5;
      else if (nextMonth >= 6 && nextMonth < 9) nextMonth = 8;
      else nextMonth = 11;

      date = date.date(1);
      date = date.month(nextMonth);

      const quarterEnd = date.daysInMonth();
      date = date.date(quarterEnd);
    }

    if (watchFrequency === "quadrimesterly") {
      const currentMonth = date.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 4) nextMonth = 3;
      else if (nextMonth >= 4 && nextMonth < 8) nextMonth = 7;
      else nextMonth = 11;

      date = date.date(1);
      date = date.month(nextMonth);

      const quarterEnd = date.daysInMonth();
      date = date.date(quarterEnd);
    }

    if (watchFrequency === "semi-annually") {
      const currentMonth = date.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 6) nextMonth = 5;
      else nextMonth = 11;

      date = date.date(1);
      date = date.month(nextMonth);

      const quarterEnd = date.daysInMonth();
      date = date.date(quarterEnd);
    }

    return {
      date: date.format("YYYY-MM-DD"),
      visibleDate: date.format("MM/DD/YYYY"),
      lastDay
    };
  }, [watchFrequency, watchBase]);

  // Reset Frequency when switching from custom frequency
  useEffect(() => {
    if (hydratingFrequencyRef.current) return;
    setValue("recurrence", {
      interval: 1,
      base: "days",
      endDate: null,
      ends: NEVER,
      days: null
    });
  }, [watchFrequency, setValue]);

  // Initial values on frequency change
  useEffect(() => {
    if (hydratingFrequencyRef.current) return;
    if (watchFrequency) {
      if (watchFrequency === "monthly") setValue("end", true);
      else setValue("end", undefined);

      if (["weekly", "bi-weekly"].includes(watchFrequency) || watchBase === "weeks")
        setValue("recurrence.days", ["Sun"]);

      setValue("start", defaultDate);
    }
  }, [watchFrequency, defaultDate, setValue, watchBase]);

  useEffect(() => {
    const snapshot = hydratingFrequencyRef.current;
    if (!snapshot) return;
    const frequencyMatches = (watchFrequency ?? "") === snapshot.frequency;
    const baseMatches = (watchBase ?? "days") === snapshot.base;
    const startMatches = (watchStartDate ?? null) === snapshot.startDate;
    if (frequencyMatches && baseMatches && startMatches) {
      hydratingFrequencyRef.current = null;
    }
  }, [watchFrequency, watchBase, watchStartDate]);

  // Keep weekday selection aligned with start date (and vice versa) for week-based freqs.
  // Skip setValue when values already match so we don't churn array/date refs and re-enter
  // the form↔state sync loop (especially with per-state names expanded).
  const prevWatchStartDate = usePrevious(watchStartDate);
  useEffect(() => {
    // Avoid overwriting restored weekdays while frequency hydration is in flight.
    if (hydratingFrequencyRef.current) return;
    const startDateWeekday = dayjs(watchStartDate).day();
    if (
      (watchBase === "weeks" || watchFrequency === "weekly" || watchFrequency === "bi-weekly") &&
      exists(startDateWeekday) &&
      DAY_NAMES_DAYJS[startDateWeekday] &&
      prevWatchStartDate !== watchStartDate
    ) {
      const targetWeekday = toTitleCase(DAY_NAMES_DAYJS[startDateWeekday]);

      if (
        (!watchDueDays || watchFrequency === "weekly" || watchFrequency === "bi-weekly") &&
        exists(startDateWeekday)
      ) {
        if (!(watchDueDays?.length === 1 && watchDueDays[0] === targetWeekday)) {
          setValue("recurrence.days", [targetWeekday]);
        }
      } else if (targetWeekday && watchDueDays && !watchDueDays.includes(targetWeekday)) {
        setValue(
          "recurrence.days",
          [...watchDueDays, targetWeekday].sort(
            (a, b) => DAY_TO_IDX_DAYJS[a.toLowerCase()] - DAY_TO_IDX_DAYJS[b.toLowerCase()]
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchBase, watchStartDate, setValue]);

  const prevWatchDueDays = usePrevious(watchDueDays);
  useEffect(() => {
    // Avoid shifting restored start dates before hydratingFrequencyRef clears.
    if (hydratingFrequencyRef.current) return;
    if (
      (watchBase === "weeks" || watchFrequency === "weekly" || watchFrequency === "bi-weekly") &&
      watchDueDays?.length > 0 &&
      prevWatchDueDays !== watchDueDays
    ) {
      const startDateWeekday = dayjs(watchStartDate).day();
      if (watchDueDays.includes(IDX_TO_DAY_DAYJS[startDateWeekday])) return;

      let i = dayjs().day();
      let hasResetToSunday = false;
      while (!watchDueDays.includes(IDX_TO_DAY_DAYJS[i])) {
        if (i === 6) {
          // prevents infinite loop if for whatever reason watchDueDays does not contain any valid days
          if (hasResetToSunday) break;
          hasResetToSunday = true;
          i = 0;
        } else i++;
      }

      let start = dayjs().day(i);

      if (start.isBefore(dayjs())) start = start.add(1, "week");

      if (!start.isSame(dayjs(watchStartDate), "day")) {
        setValue("start.date", start.format("YYYY-MM-DD"));
        setValue("start.visibleDate", start.format("MM/DD/YYYY"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchBase, watchDueDays, setValue]);

  const buildPreview = () => {
    const checksheetBuilder = newFromBuilder(
      builder.allIds,
      {...builder},
      {allIds: [], byId: {}},
      true
    );
    setPreview({builder: checksheetBuilder, name: currentName, frequency: watchFrequency});
    setShowPreview(true);
  };

  const handleShowModalFormula = element => {
    if (element.type === "generated") setShowModalRedirectFormula(true);
    else setShowModalFormula(true);
  };

  const handleShowModalElapsed = element => {
    if (element.type === "generated") setShowModalRedirectElapsed(true);
    else {
      setElapsedMode("create");
      setShowModalElapsed(true);
    }
  };

  const firstDueDate = useMemo(() => {
    if (startPreview && watchAvailability && watchFrequency === CUSTOM_OPTION.name) {
      let due = convertToUserTimezone(startPreview, settings.timezone)
        .add(1, watchBase.includes("month") || watchInterval === 1 ? "month" : "year")
        .subtract(1, "minute");

      if (watchLastDay) due = due.date(due.daysInMonth());

      return due.format("MM/DD/YYYY, h:mm A");
    }
    return null;
  }, [
    startPreview,
    watchAvailability,
    watchBase,
    watchFrequency,
    watchInterval,
    watchLastDay,
    settings
  ]);

  const minDateFromAvailabilityWindow = useMemo(() => {
    if (watchInterval && watchAvailability && watchBase) {
      let due = dayjs();
      if (watchInterval === 1 && watchBase.includes("year"))
        due = due.add(
          12 - watchAvailability,
          watchBase.includes("month") || watchInterval === 1 ? "month" : "year"
        );
      else
        due = due.add(
          watchInterval - watchAvailability,
          watchBase.includes("month") || watchInterval === 1 ? "month" : "year"
        );

      if (watchLastDay) due = due.date(due.daysInMonth());

      return due.format("YYYY-MM-DD");
    }
    return null;
  }, [watchBase, watchInterval, watchLastDay, watchAvailability]);

  const minDate = useMemo(() => {
    if (minDateFromAvailabilityWindow) return minDateFromAvailabilityWindow;
    return dayjs().format("YYYY-MM-DD");
  }, [minDateFromAvailabilityWindow]);

  useEffect(() => {
    // Skip while hydrating so a minDate clamp cannot diverge start from the
    // snapshot and leave hydratingFrequencyRef stuck for the rest of the mount.
    if (hydratingFrequencyRef.current) return;
    if (dayjs(watchStartDate).isBefore(dayjs(minDate))) {
      setValue("start.date", minDate);
      setValue("start.visibleDate", dayjs(minDate).format("MM/DD/YYYY"));
      setValue("start.lastDay", false);
    }
  }, [watchStartDate, minDate, setValue]);

  const handleChecksheetStateSelect = t => {
    const targetId = `${t.name}`;
    if (targetId === `${currentStateId}`) return;
    setCurrentStateId(targetId);
  };

  const confirmAddChecksheetState = selectedStateId => {
    setStates(prev => {
      const temp = {
        ...prev,
        [`${selectedStateId}`]: {
          ...prev[DEFAULT_STATE.id],
          frequency: {...prev[DEFAULT_STATE.id].frequency},
          builder: deepCopyBuilder(prev[DEFAULT_STATE.id].builder)
        }
      };
      persistStates(temp);
      return temp;
    });
    setCurrentStateId(`${selectedStateId}`);
  };

  const getNewStartDate = useCallback(
    offset => {
      // if offset is 1, shift to next
      let newStartDate = dayjs(watchStartDate);
      if (watchFrequency === "semi-monthly") {
        let midCurrentMonth = Math.floor(newStartDate.daysInMonth() / 2);
        if (newStartDate.date() === midCurrentMonth) {
          if (offset === 1) newStartDate = newStartDate.date(newStartDate.daysInMonth());
          else newStartDate = newStartDate.date(1).add(-1, "day");
        } else if (offset === 1) newStartDate = newStartDate.add(1, "month").date(midCurrentMonth);
        else {
          newStartDate = newStartDate.add(-1, "month");
          midCurrentMonth = Math.floor(newStartDate.daysInMonth() / 2);
          newStartDate = newStartDate.date(midCurrentMonth);
        }
      } else if (watchFrequency === "quarterly") {
        let nextMonth = newStartDate.month();
        let nextYear = newStartDate.year();
        if (nextMonth === 2) {
          nextMonth = offset === 1 ? 5 : 11;
          if (offset === -1) nextYear -= 1;
        } else if (nextMonth === 5) nextMonth = offset === 1 ? 8 : 2;
        else if (nextMonth === 8) nextMonth = offset === 1 ? 11 : 5;
        else {
          nextMonth = offset === 1 ? 2 : 8;
          if (offset === 1) nextYear += 1;
        }
        newStartDate = newStartDate.year(nextYear).month(nextMonth).date(1);
        const quarterEnd = newStartDate.daysInMonth();
        newStartDate = newStartDate.date(quarterEnd);
      } else if (watchFrequency === "quadrimesterly") {
        let nextMonth = newStartDate.month();
        let nextYear = newStartDate.year();
        if (nextMonth === 3) {
          nextMonth = offset === 1 ? 7 : 11;
          if (offset === -1) nextYear -= 1;
        } else if (nextMonth === 7) nextMonth = offset === 1 ? 11 : 3;
        else {
          nextMonth = offset === 1 ? 3 : 7;
          if (offset === 1) nextYear += 1;
        }
        newStartDate = newStartDate.year(nextYear).month(nextMonth).date(1);
        const quadrimesterEnd = newStartDate.daysInMonth();
        newStartDate = newStartDate.date(quadrimesterEnd);
      } else if (watchFrequency === "semi-annually") {
        let nextMonth = newStartDate.month();
        let nextYear = newStartDate.year();
        if (nextMonth === 5) {
          nextMonth = 11;
          if (offset === -1) nextYear -= 1;
        } else {
          nextMonth = 5;
          if (offset === 1) nextYear += 1;
        }
        newStartDate = newStartDate.year(nextYear).month(nextMonth).date(1);
        const halfYearEnd = newStartDate.daysInMonth();
        newStartDate = newStartDate.date(halfYearEnd);
      }

      return newStartDate;
    },
    [watchFrequency, watchStartDate]
  );

  const shiftStart = useCallback(
    (offset = 1) => {
      const newStartDate = getNewStartDate(offset);

      setValue("start.date", newStartDate.format("YYYY-MM-DD"));
      setValue("start.visibleDate", newStartDate.format("MM/DD/YYYY"));
    },
    [setValue, getNewStartDate]
  );

  const transferElement = targetElement => {
    setTarget({...targetElement});
    setShowModalTransfer(true);
  };

  if (!roleCanAccessResource("facility_checksheet", "create"))
    return <Navigate to="/unauthorized" />;

  return (
    <Page hasMenu clipped isFlex compact>
      <Room name="checksheet-builder" active={active} setActive={setActive} />

      {facility?.name && (
        <FacilityPageHeader
          facility={facility}
          path="/checksheet-builder"
          reloadData={() => setFacility(null)}
        />
      )}

      <Inline start fullWidth>
        <TopPageLeft showFacility noPad>
          <HeadingMedium>Checksheet Builder</HeadingMedium>
        </TopPageLeft>
        <TopPageRight>
          <Inline end>
            {Object.keys(states).length < 2 && facilityStates?.length && (
              <AddState
                type="button"
                data-testid="checksheet.addState"
                onClick={() => setShowModalAddState(true)}>
                <FontAwesomeIcon icon={faPlus} /> State
              </AddState>
            )}
            <Button type="button" onClick={() => setShowFacility(prev => !prev)}>
              {showFacility ? "Menu Options" : "Facility Builder"}
            </Button>
          </Inline>
        </TopPageRight>
      </Inline>

      <Wrapper fullWidth>
        {showFacility ? (
          <PageLeft showFacility>
            <HeadingMedium>Facility</HeadingMedium>
            <FacilityBuilder sidecar />
          </PageLeft>
        ) : (
          <PageLeft>
            {loadingFacility ? (
              <Loader data-testid="checksheet.loader" />
            ) : (
              <>
                <FormProvider {...form}>
                  <StyledForm onSubmit={handleSubmit(handlePublish)} noValidate>
                    {renderNameField()}
                    {!stateId && (
                      <FreqWrapper>
                        {frequencies?.length > 0 && (
                          <InputSelect
                            name="frequency"
                            placeholder="Frequency..."
                            testId="checksheet.frequency"
                            options={frequencySelectOptions}
                            required
                          />
                        )}
                      </FreqWrapper>
                    )}

                    {watchFrequency === CUSTOM_OPTION.name && (
                      <CustomGroup fitcontent>
                        <FormField>
                          <Label bold>REPEAT EVERY</Label>
                          <CustomGroup>
                            <InputNumber
                              testId="interval"
                              name="recurrence.interval"
                              min={1}
                              max={10}
                              maxWidth
                              required={false}
                              disableInverse
                              hideError
                            />
                            <BaseSelectWrapper>
                              <InputSelect
                                testId="base"
                                fullWidth
                                name="recurrence.base"
                                options={watchInterval > 1 ? BASE_OPTIONS_PLURAL : BASE_OPTIONS}
                              />
                            </BaseSelectWrapper>
                          </CustomGroup>
                        </FormField>
                        {showAvailable(watchedFreqObj) && (
                          <InputSelect
                            name="recurrence.availability"
                            label="AVAILABLE"
                            options={availableOptions}
                            placeholder="Always"
                          />
                        )}
                        <InputError errors={errors} name="recurrence.interval" />
                      </CustomGroup>
                    )}

                    {(watchFrequency === "weekly" ||
                      watchFrequency === "bi-weekly" ||
                      watchBase === "weeks") && (
                      <InputDay
                        name="recurrence.days"
                        restrictOne={watchFrequency !== CUSTOM_OPTION.name}
                        restrictWarning={
                          watchFrequency === CUSTOM_OPTION.name
                            ? "Multiple day selection not currently supported for intervals longer than 1 week"
                            : null
                        }
                      />
                    )}

                    {watchFrequency && watchFrequency !== "none" && (
                      <DatesWrapper>
                        <DateWrapper>
                          <Label bold>
                            AVAILABLE DATE&nbsp;
                            <Help>
                              Available Date is when the checksheet first becomes available to
                              complete.
                            </Help>
                          </Label>
                          <Input
                            type="text"
                            value={
                              startPreview &&
                              prettyDateInUserTimezone(startPreview, null, "MM/DD/YYYY, h:mm A")
                            }
                            disabled
                          />
                        </DateWrapper>
                        {watchAvailability && (
                          <DateWrapper>
                            <Label bold>
                              FIRST DUE DATE&nbsp;
                              <Help>
                                First Due Date is when it is first due. For recurring frequencies,
                                Interval End Date sets the end of the current recurrence and future
                                due dates are calculated from it.
                              </Help>
                            </Label>
                            <Input type="text" value={firstDueDate} disabled />
                          </DateWrapper>
                        )}
                        <DateWrapper>
                          <InputCalendar
                            name="start"
                            label={watchAvailability ? "INTERVAL END DATE" : "FIRST DUE DATE"}
                            showLastDay={hasLastDayOption}
                            min={minDate}
                            disabled={
                              watchFrequency === "semi-monthly" ||
                              watchFrequency === "quarterly" ||
                              watchFrequency === "quadrimesterly" ||
                              watchFrequency === "semi-annually"
                            }
                          />
                        </DateWrapper>
                        {(watchFrequency === "semi-monthly" ||
                          watchFrequency === "quarterly" ||
                          watchFrequency === "quadrimesterly" ||
                          watchFrequency === "semi-annually") && (
                          <Inline>
                            <ArrowButton
                              onClick={() => shiftStart(-1)}
                              type="button"
                              disabled={getNewStartDate(-1).isBefore(minDate)}>
                              <FontAwesomeIcon icon={faArrowLeft} />
                            </ArrowButton>
                            <ArrowButton onClick={() => shiftStart(1)} type="button">
                              <FontAwesomeIcon icon={faArrowRight} />
                            </ArrowButton>
                          </Inline>
                        )}
                      </DatesWrapper>
                    )}

                    {frequencyWarning && (
                      <WarningWrapper>
                        <WarningHeader>Warning!</WarningHeader>&nbsp;{frequencyWarning}
                      </WarningWrapper>
                    )}

                    <Options>
                      <SaveDraft
                        type="button"
                        data-testid="checksheet.saveDraft"
                        onClick={() => {
                          handlePublish(true);
                        }}
                        loading={loadingDraft ? 1 : 0}>
                        {loadingDraft && <ButtonLoader />}
                        Create as Draft
                      </SaveDraft>
                      <Submit
                        type="submit"
                        data-testid="checksheet.submit"
                        loading={loadingPublish ? 1 : 0}>
                        {loadingPublish && <ButtonLoader />}
                        Create and Publish
                      </Submit>
                    </Options>
                  </StyledForm>
                </FormProvider>
                {facility && (
                  <>
                    <MemberRestrict
                      restrictTo={restrictTo}
                      external={external}
                      showModalEdit={setShowModalMemberRestrict}
                      conflicts={visibilityConflicts}
                    />
                    <ChecksheetRestrictions
                      showModalEdit={setShowModalRestriction}
                      restrictions={checksheetRestrictions}
                      state={state}
                      conflicts={visibilityConflicts}
                    />
                    <Notifications
                      showModalEdit={setShowModalNotification}
                      facility={facility}
                      notifications={fieldNotifications}
                    />
                    <MissingElements
                      initialLoad
                      insertElement={setElementToInsert}
                      facility={facility}
                      currentBuilder={builder || {allIds: [], byId: {}}}
                      refresh={refreshMissing}
                      setRefresh={setRefreshMissing}
                      setShowModalMissing={setShowModalAddFBElement}
                    />
                    {roleCanAccessResource("component", "view") && (
                      <ComponentTemplates
                        setTemplate={setTemplate}
                        setShowModalTemplate={setShowModalTemplate}
                        setRefresh={setRefreshTemplates}
                        refresh={refreshTemplates}
                        setTemplateList={setTemplateList}
                        templateList={templateList}
                        hasPrimaryAddress={!!facility?.primaryAddress}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </PageLeft>
        )}
        <PageRight>
          <Builder data-testid="checksheet.preview">
            <Menu>
              <MenuHeading>
                <PreviewNameRow>
                  <PreviewNameHeading>
                    <Heading>
                      <Abbr title={currentName.toUpperCase()}>
                        {currentName.length > 0 ? currentName.toUpperCase() : "Name..."}
                      </Abbr>
                    </Heading>
                  </PreviewNameHeading>
                  <PreviewHeaderMeta>
                    {currentPreviewFrequency &&
                      currentPreviewFrequency !== "none" &&
                      currentPreviewFrequency.length > 0 && <Pill>{currentPreviewFrequency}</Pill>}
                    {facilityStates && checksheetStateSelectOptions?.length > 1 && (
                      <PreviewChecksheetStateSelect data-testid="checksheet.previewStateSelect">
                        <Select
                          options={checksheetStateSelectOptions}
                          selection={`${currentStateId}`}
                          selectionLabel={currentState?.name || DEFAULT_STATE.name.toUpperCase()}
                          setSelection={handleChecksheetStateSelect}
                          pills
                          compact
                          color={currentState?.color ? `#${currentState.color}` : undefined}
                          inverted={false}
                          menuAlignEnd
                        />
                      </PreviewChecksheetStateSelect>
                    )}
                  </PreviewHeaderMeta>
                </PreviewNameRow>
              </MenuHeading>
              <MenuButtons>
                {builder?.allIds?.length > 0 && (
                  <PreviewActionButton
                    type="button"
                    data-testid="checksheet.bulkOperations"
                    onClick={() => setShowModalBulk(true)}>
                    <FontAwesomeIcon icon={faGears} />
                    &nbsp;
                    <span>Bulk operations</span>
                  </PreviewActionButton>
                )}
                <ActionsMenuWrapper ref={actionsMenuRef}>
                  <ActionMenuButton
                    title="More Actions"
                    type="button"
                    plain
                    data-testid="checksheet.moreActions"
                    onClick={() => setShowActionsMenu(prev => !prev)}>
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </ActionMenuButton>
                  {showActionsMenu && (
                    <ActionsMenu>
                      <ActionsMenuScroll>
                        {builder && facility?.builder?.allIds?.length > 0 && (
                          <ActionItem
                            type="button"
                            danger={hasElements}
                            data-testid={hasElements ? "checksheet.reset" : "checksheet.facilityBuilder"}
                            onClick={() => {
                              setShowActionsMenu(false);
                              handleUseFacilityBuilder();
                            }}>
                            <FontAwesomeIcon icon={hasElements ? faRotateLeft : faMapMarked} />
                            <span>{hasElements ? "Reset builder" : "Facility Builder"}</span>
                          </ActionItem>
                        )}
                        <ActionItem
                          type="button"
                          onClick={() => {
                            setShowActionsMenu(false);
                            setShowFilterModal(true);
                          }}>
                          <FontAwesomeIcon icon={faFilter} />
                          <span>
                            Filter fields
                            {filterCount > 0 ? ` (${filterCount})` : ""}
                          </span>
                        </ActionItem>
                        {hasToggledOn && (
                          <ActionItem
                            type="button"
                            onClick={() => {
                              setShowActionsMenu(false);
                              buildPreview();
                            }}>
                            <FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
                            <span>Preview checksheet</span>
                          </ActionItem>
                        )}
                        {hasToggledOff !== null && hasElements && (
                          <ActionItem
                            type="button"
                            title={`Toggle all elements in the builder ${
                              hasToggledOff ? "on" : "off"
                            }`}
                            data-testid="checksheet.toggleAll"
                            disabled={loadingToggleAll}
                            onClick={() => {
                              setShowActionsMenu(false);
                              toggleAll();
                            }}>
                            <FontAwesomeIcon icon={hasToggledOff ? faToggleOn : faToggleOff} />
                            <span>Toggle Elements {hasToggledOff ? "On" : "Off"}</span>
                          </ActionItem>
                        )}
                        {Object.keys(states).some(id => id !== DEFAULT_STATE.id) &&
                          `${currentStateId}` !== DEFAULT_STATE.id && (
                            <ActionItem
                              type="button"
                              danger
                              onClick={() => {
                                setShowActionsMenu(false);
                                setShowModalConfirmDeleteState(true);
                              }}>
                              <FontAwesomeIcon icon={faTrash} />
                              <span>Delete state...</span>
                            </ActionItem>
                          )}
                      </ActionsMenuScroll>
                    </ActionsMenu>
                  )}
                </ActionsMenuWrapper>
              </MenuButtons>
            </Menu>

            <Errors>
              {external && restrictTo?.length > 0 && (
                <Warning>
                  Warning:&nbsp;
                  <span>
                    Checksheet available for completion to&nbsp;
                    {restrictTo
                      .map(({firstName, lastName, label}) =>
                        firstName ? `${firstName} ${lastName}` : label
                      )
                      .join(", ")}
                    &nbsp;via email only.
                  </span>
                </Warning>
              )}
              {watchFrequency === "none" && (
                <Warning>
                  Warning:
                  <span>
                    Checksheet will be created without due date. Completed on an as needed basis.
                  </span>
                </Warning>
              )}
              {error && error[currentStateId] && (
                <Error>
                  Invalid builder, please make sure all Components and Groups have at least one
                  field or there is one checksheet level field.
                </Error>
              )}
            </Errors>

            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
              <Droppable
                droppableId="facility.builder"
                isDropDisabled={activeDroppableId !== "facility.builder"}>
                {provided => (
                  <BuilderWrapper {...provided.droppableProps} ref={provided.innerRef}>
                    <RenderChecksheetBuilder
                      builder={builder}
                      otherBuilder={otherBuilder}
                      state={state}
                      isState={currentStateId !== DEFAULT_STATE.id}
                      filterResult={filterResult}
                      setBuilder={saveBuilder}
                      activeDroppableId={activeDroppableId}
                      setTarget={setTarget}
                      cloneElement={cloneElement}
                      transferElement={transferElement}
                      setShowModalSaveTemplate={setShowModalSaveTemplate}
                      togglePreview={togglePreview}
                      setHasToggledOff={setHasToggledOff}
                      templates={templateList}
                      setShowModalHelp={setShowModalHelp}
                      setShowModalEdit={setShowModalEdit}
                      setShowModalAdd={setShowModalAdd}
                      setShowModalDelete={setShowModalDelete}
                      setShowModalConditional={setShowModalConditional}
                      setShowModalFormula={handleShowModalFormula}
                      setShowModalElapsed={handleShowModalElapsed}>
                      {provided.placeholder}
                    </RenderChecksheetBuilder>
                  </BuilderWrapper>
                )}
              </Droppable>
            </DragDropContext>
          </Builder>
        </PageRight>
      </Wrapper>

      {/* Modals */}
      {leaveWarning && (
        <Modal
          visible={leaveWarning}
          setVisible={s => {
            if (!s) {
              setQueuedAction(undefined);
              setLeaveWarning(false);
            }
          }}>
          <Text data-testid="checksheet.leaveWarning">
            You have unsaved changes. Would you like to continue (changes will be saved) or discard?
          </Text>
          <br />
          <Inline>
            <Button data-testid="checksheet.leaveCache" onClick={handleCacheAndLeave}>
              Continue
            </Button>
            <WarningButton data-testid="checksheet.leaveDiscard" onClick={handleDiscardAndLeave}>
              Discard
            </WarningButton>
            <Button
              data-testid="checksheet.leaveCancel"
              onClick={() => {
                setQueuedAction(undefined);
                setLeaveWarning(false);
              }}>
              Cancel
            </Button>
          </Inline>
        </Modal>
      )}

      {showModalBulk && (
        <ModalBulk
          visible={showModalBulk}
          setVisible={setShowModalBulk}
          save={(b, u) => saveBuilder(b, u)}
          builder={builder}
          hasPrimaryAddress={!!facility?.primaryAddress}
          notifications={fieldNotifications}
          units={units}
        />
      )}

      {showModalTransfer && target && (
        <ModalTransfer
          visible={showModalTransfer}
          setVisible={setShowModalTransfer}
          save={b => {
            const stateIdToUse = currentStateId === DEFAULT_STATE.id ? stateId : DEFAULT_STATE.id;
            setCurrentStateId(stateIdToUse);
            saveBuilder(b, undefined, undefined, undefined, stateIdToUse);
          }}
          sourceBuilder={builder}
          destBuilder={otherBuilder}
          targetElement={target}
          state={state}
          isState={currentStateId !== DEFAULT_STATE.id}
        />
      )}

      {showModalHelp && target && (
        <ModalHelp visible={showModalHelp} setVisible={setShowModalHelp} target={target} />
      )}
      {showModalEdit && target && (
        <ModalEditElement
          visible={showModalEdit}
          setVisible={setShowModalEdit}
          targetElement={target}
          persistEdit={handleEdit}
          builder={builder}
          otherBuilder={otherBuilder}
          state={state}
          isState={currentStateId !== DEFAULT_STATE.id}
          units={units}
          templateLabel={
            target.templateId && templateList ? getTemplateLabel(target.templateId) : null
          }
          editing={false}
        />
      )}

      {showModalAdd && target && (
        <ModalAddField
          visible={showModalAdd}
          setVisible={setShowModalAdd}
          addFields={handleAddMultipleFields}
          parent={target}
          builder={builder}
          units={units}
        />
      )}

      {showModalRedirectFormula && (
        <ModalRedirect
          visible={showModalRedirectFormula}
          setVisible={setShowModalRedirectFormula}
          buttonOptions={[
            {
              action: () => {
                setFormulaMode("create");
                setShowModalFormula(true);
                setShowModalRedirectFormula(false);
              },
              description: "Create formula based on this field",
              icon: faPlus
            },
            {
              action: () => {
                setFormulaMode("edit");
                setShowModalFormula(true);
                setShowModalRedirectFormula(false);
              },
              description: "Edit existing formula field",
              icon: faEdit,
              disabledAction: target.shared
                ? () => addToast("Must edit shared fields in the Facility Builder")
                : undefined
            }
          ]}
        />
      )}

      {showModalRedirectElapsed && (
        <ModalRedirect
          visible={showModalRedirectElapsed}
          setVisible={setShowModalRedirectElapsed}
          buttonOptions={[
            {
              action: () => {
                setFormulaMode("create");
                setShowModalFormula(true);
                setShowModalRedirectElapsed(false);
              },
              description: "Create formula based on this field",
              icon: faPlus
            },
            {
              action: () => {
                setElapsedMode("edit");
                setShowModalElapsed(true);
                setShowModalRedirectElapsed(false);
              },
              description: "Edit existing elapsed time field",
              icon: faEdit
            }
          ]}
        />
      )}

      {showModalFormula && target && (
        <ModalFormula
          visible={showModalFormula}
          setVisible={s => {
            if (!s) setFormulaMode("create");
            setShowModalFormula(s);
          }}
          targetElement={target}
          mode={formulaMode}
          page="builder"
          builder={builder}
          units={units}
          hasPrimaryAddress={!!facility?.primaryAddress}
          save={handleFormula}
        />
      )}

      {showModalElapsed && target && (
        <ModalElapsed
          visible={showModalElapsed}
          setVisible={s => {
            if (!s) setElapsedMode("create");
            setShowModalElapsed(s);
          }}
          targetElement={target}
          mode={elapsedMode}
          builder={builder}
          save={handleFormula}
        />
      )}

      {showModalConditional && target && (
        <ModalConditionals
          visible={showModalConditional && !!target}
          setVisible={s => {
            if (!s) setTarget(null);
            setShowModalConditional(s);
          }}
          targetElement={target}
          save={handleConditional}
          builder={builder}
          hasPrimaryAddress={!!facility?.primaryAddress}
        />
      )}

      {showModalDelete && target && (
        <ModalDeleteElement
          visible={showModalDelete}
          setVisible={setShowModalDelete}
          facilityId={facility.id}
          builder={builder}
          notifications={fieldNotifications}
          persistDelete={handleDelete}
          targetElement={target}
          isState={currentStateId !== DEFAULT_STATE.id}
        />
      )}

      {showModalAddFBElement && (
        <ModalAddToBuilder
          visible={showModalAddFBElement}
          setShowModal={setShowModalAddFBElement}
          builder={builder}
          facility={facility}
          saveBuilder={saveBuilder}
          element={elementToInsert}
          setRefreshMissing={setRefreshMissing}
        />
      )}

      {showModalTemplate && (
        <ModalApplyTemplate
          builder={builder}
          visible={showModalTemplate}
          setShowModal={setShowModalTemplate}
          template={template}
          saveBuilder={saveBuilder}
          recursePreview={recursePreview}
          units={units}
          setUnits={setUnits}
        />
      )}

      {showModalSaveTemplate && (
        <ModalSaveTemplate
          visible={showModalSaveTemplate}
          setShowModal={setShowModalSaveTemplate}
          targetName={target ? target.name : null}
          targetLabel={target ? target.label : null}
          saveTemplate={addTemplate}
        />
      )}

      {showPreview && (
        <ModalChecksheetPreview
          checksheet={preview}
          visible={showPreview}
          setVisible={setShowPreview}
        />
      )}

      {showModalNotification && (
        <ModalNotifications
          visible={showModalNotification}
          setVisible={setShowModalNotification}
          notifications={fieldNotifications}
          setNotifications={setFieldNotifications}
          handleSave={saveNotifications}
          states={states}
          state={state}
        />
      )}

      {showModalRestriction && (
        <ModalChecksheetRestrictions
          visible={showModalRestriction}
          setVisible={setShowModalRestriction}
          checksheetRestrictions={checksheetRestrictions}
          handleSave={setChecksheetRestrictions}
          builder={builder}
          facilityId={facility.id}
          state={state}
          states={states}
          restrictTo={restrictTo}
          conflicts={visibilityConflicts}
        />
      )}

      {showModalMemberRestrict && (
        <ModalMemberRestrict
          visible={showModalMemberRestrict}
          setVisible={setShowModalMemberRestrict}
          restrictTo={restrictTo}
          setRestrictTo={setRestrictTo}
          external={external}
          setExternal={setExternal}
          allowExternal={allowExternal}
          facilityId={facility.id}
          checksheetRestrictions={checksheetRestrictions}
        />
      )}

      {showFilterModal && (
        <ModalFilter
          visible={showFilterModal}
          setVisible={setShowFilterModal}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {showModalConfirmDeleteState && (
        <ModalDelete
          visible={showModalConfirmDeleteState}
          setVisible={setShowModalConfirmDeleteState}
          confirmDelete={handleDeleteState}
          title="Delete State?"
          prompt="Are you sure you want to delete this state? This action cannot be undone."
        />
      )}
      {showModalAddState && (
        <ModalAddState
          visible={showModalAddState}
          setVisible={setShowModalAddState}
          facilityStates={facilityStates || []}
          checksheetStates={states}
          onConfirm={confirmAddChecksheetState}
        />
      )}
    </Page>
  );
};

// Style Overrides
const Wrapper = styled(Inline)`
  ${flex("row", "wrap", "start", "stretch")};
  gap: 0;

  ${bp(3)} {
    flex-wrap: nowrap;
    height: 100%;
    overflow: hidden;
    position: relative;
  }
`;

const TopPageLeft = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  max-height: max-content;
  margin-top: ${pad}px;

  ${bp(3)} {
    flex: 0 0 auto;
    ${({showFacility}) =>
      showFacility
        ? css`
            width: calc(40% - (${`${pad}px`}));
            max-width: 380px;
          `
        : css`
            width: 40%;
            max-width: 390px;
          `}
  }
`;

const TopPageRight = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
  margin-left: auto;
  margin-top: ${pad}px;
  max-height: max-content;

  ${bp(3)} {
    position: relative;
  }
`;

const PreviewNameRow = styled.div`
  width: fit-content;
  max-width: 100%;
  display: flex;
  flex-wrap: nowrap;
  min-width: 0;
  align-items: center;
  gap: ${pad}px;
  margin-left: 0;
`;

const PreviewNameHeading = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;

  ${Heading} {
    min-width: 0;
    flex: 1 1 auto;
  }
`;

const PreviewChecksheetStateSelect = styled.div`
  flex: 0 0 auto;
`;

const PreviewHeaderMeta = styled(Inline)`
  flex: 0 0 auto;
  flex-shrink: 0;
  align-items: center;
  gap: ${pad}px;

  > div {
    width: max-content;
    min-width: 0;
  }

  ${Pill} {
    background: ${({theme}) => theme.primary};
  }
`;

const FreqWrapper = styled(FormField)`
  ${flex("row", "nowrap", "start", "space-between")};
  width: 100%;
  gap: ${pad}px;
  margin-bottom: ${pad / 2}px;

  select {
    max-width: 100%;
  }
`;

const PageLeft = styled.div`
  position: relative;
  width: 100%;
  max-height: max-content;
  overflow: auto;
  ${scrollbar};

  ${({showFacility}) =>
    showFacility &&
    css`
      padding: ${pad}px;
      border: ${border} solid ${({theme}) => theme.secondary};
      border-radius: ${radius};
    `}

  ${bp(3)} {
    flex: 0 0 auto;
    min-width: 0;
    /* using combination of margin and padding centers the scrollbar when it appears */
    margin: 0 ${pad}px ${pad}px 0;
    padding-right: ${pad}px;

    ${({showFacility}) =>
      showFacility
        ? css`
            max-width: 380px;
            width: calc(40% - (${`${pad}px`}));
            height: calc(100% - (${`${pad}px`}));
          `
        : css`
            width: 40%;
            max-width: 390px;
            height: 100%;
          `}
  }
`;

const PageRight = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  width: 100%;

  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  margin-top: ${pad}px;
  max-height: max-content;
  padding-bottom: ${pad}px;

  ${bp(3)} {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    height: calc(100% - (${`${pad}px`}));
    overflow: auto;
    margin-bottom: ${pad * 2}px;

    > div > div:last-of-type {
      padding-bottom: ${pad}px;
    }
  }
`;

const Submit = styled(ButtonFull)`
  position: relative;
  width: calc(50% - 5px);
  color: ${({theme}) => theme.primary};
  margin: ${pad}px 0 ${pad * 1.5}px;
`;

const SaveDraft = styled(Submit)`
  color: ${({theme}) => theme.warning};
  flex: 1;
`;

const Builder = styled.div`
  position: relative;
  min-width: 0;
  min-height: max-content;
`;

const BuilderWrapper = styled.div`
  padding: 0 ${pad}px;
  min-width: 0;
`;

const Menu = styled(StickyWrapper)`
  z-index: 3;
  ${flex("column", "nowrap")};
  min-width: 0;

  background-color: ${({theme}) => theme.tertiary};

  padding: ${pad}px ${pad}px ${pad / 2}px;

  top: 0;
  flex: 1;
  margin: 0;

  ${bp(1)} {
    ${flex("row", "nowrap", "space-between", "center")};
  }

  ${Heading} {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const MenuHeading = styled(Inline)`
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  justify-content: start;
  align-items: center;
  gap: ${pad / 2}px;
`;

const MenuButtons = styled(Inline)`
  width: auto;
  flex: 0 0 auto;
  justify-content: end;
  align-items: center;
  gap: ${pad / 2}px;
  margin-left: ${pad / 2}px;
`;

const PreviewActionButton = styled(Button)`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 4}px;
  white-space: nowrap;
`;

const ActionsMenuWrapper = styled.div`
  position: relative;
`;

const ActionMenuButton = styled(Button)`
  padding: ${pad / 2}px 0 ${pad / 2}px ${pad}px;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + ${pad / 2}px);
  right: 0;
  min-width: 220px;
  max-height: 60vh;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("higher")};
  overflow: hidden;

  &:before,
  &:after {
    content: "";
    z-index: ${z("above")};
    position: absolute;
    left: 0;
    width: 100%;
    height: ${pad}px;
  }

  &:after {
    top: 0;
    background: ${({theme}) =>
      `linear-gradient(to bottom, ${`${hexToRgba(theme.tertiary)} 0%`}, ${
        theme.name === "light" ? "rgba(255, 255, 255, 0) 100%" : "rgba(0, 0, 0, 0) 100%"
      })`};
  }

  &:before {
    bottom: 0;
    background: ${({theme}) =>
      `linear-gradient(to top, ${`${hexToRgba(theme.tertiary)} 0%`}, ${
        theme.name === "light" ? "rgba(255, 255, 255, 0) 100%" : "rgba(0, 0, 0, 0) 100%"
      })`};
  }
`;

const ActionsMenuScroll = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${pad / 2}px 0;
  ${scrollbar};
`;

const ActionItem = styled.button`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  background: ${({theme}) => theme.tertiary};
  color: ${({theme, danger}) => (danger ? theme.error : theme.secondary)};
  text-align: left;

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme, danger}) => (danger ? theme.error : theme.secondary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledForm = styled(Form)`
  z-index: 3;

  -webkit-position: sticky;
  position: sticky;

  background-color: ${({theme}) => theme.tertiary};

  top: 0;
  flex: 1;

  width: 100%;
  margin: 0 auto;
`;

const Options = styled(Inline)`
  gap: ${pad}px;
  width: 100%;
  align-items: stretch;
`;

const NameGroupExpanded = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto auto;
  column-gap: ${pad}px;
  row-gap: ${pad}px;
  align-items: start;
  width: 100%;
  margin-top: ${pad}px;
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${pad}px;
  width: 100%;
  margin-top: ${pad}px;
`;

const NameRowCollapsed = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: ${pad}px;
  align-items: center;
  width: 100%;
  margin-top: ${pad}px;
`;

const NameInputWrapper = styled.div`
  width: 100%;
  min-width: 0;

  div {
    margin-top: 0;
  }
`;

const NameFieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pad}px;
  width: 100%;
  min-width: 0;

  div {
    margin-top: 0;
  }

  select {
    width: 100%;
    max-width: 100%;
  }

  ${bp(3)} {
    select {
      max-width: 100%;
    }
  }
`;

const StateFrequencySelect = styled(NativeSelect)`
  width: 100%;
  max-width: 100%;

  ${bp(3)} {
    max-width: 100%;
  }
`;

const NamePill = styled(Pill)`
  flex-shrink: 0;
  width: 72px;
  justify-content: center;
  align-self: start;
`;

const NameToggleButton = styled.button`
  flex-shrink: 0;
  background-color: transparent;
  max-width: max-content;
  padding: ${pad / 2}px;

  svg {
    fill: ${({theme}) => theme.secondary};
  }

  ${NameGroupExpanded} & {
    grid-column: 3;
    grid-row: 1 / span 2;
    align-self: center;
  }
`;

const CustomGroup = styled(Inline)`
  margin-top: ${pad / 2}px;
  width: 100%;
  align-items: end;
  justify-content: start;

  div {
    margin: 0;

    ${({fitcontent}) =>
      fitcontent &&
      css`
        width: max-content;
      `}
  }

  input {
    min-width: 35px;
    width: 35px;
  }

  select {
    width: 105px;
  }

  ${bp(2)} {
    margin-right: 0;
  }
`;

const BaseSelectWrapper = styled.div`
  select {
    max-width: 88px;
  }
`;

const DateWrapper = styled(FormFieldWrapper)`
  flex: 1 1 0;
  min-width: 0;
  width: auto;

  > div {
    width: 100%;
  }

  > div > div:first-of-type {
    width: 100%;
    max-width: none;
  }

  input {
    width: 100%;
    max-width: none;
  }
`;

const DatesWrapper = styled(Inline)`
  flex-wrap: wrap;
  align-items: flex-end;
  width: 100%;
`;

const Errors = styled.div`
  margin-bottom: ${pad}px;
  margin-left: ${pad}px;

  ${Warning}, ${Error} {
    margin-bottom: ${pad / 2}px;
  }
`;

const WarningWrapper = styled(Warning)`
  margin-top: ${pad / 2}px;
  color: ${({theme}) => theme.secondary};
`;

const WarningHeader = styled.strong`
  color: ${({theme}) => theme.warning};
`;

const AddState = styled(Button)`
  margin-left: ${pad / 2}px;
`;

const WarningButton = styled(Button)`
  color: ${({theme}) => theme.error};
`;

const ArrowButton = styled.button`
  background-color: transparent;
  max-width: max-content;

  ${({disabled}) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

export default ChecksheetBuilder;
